import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Link2, Loader2, ArrowRight, ArrowLeft, CheckCircle, MapPin, Calendar } from 'lucide-react'; 
import { Button } from '../ui/button'; 
import LocationAutocomplete from '../LocationAutocomplete'; 
import { calculateVisibleQuestions } from './LeadFlow/leadVisibilityFunction';
import config from '../../config';
import api from '../../services/apiServices'; 

const MAP_LIBRARIES = ['drawing', 'places', 'marker'];

// Helper to sort options
const sortQuestionOptions = (question) => {
  if (!question || !Array.isArray(question.options)) return question;
  const sorted = [...question.options].sort((a, b) => a.order_index - b.order_index);
  return { ...question, options: sorted };
};

export default function LeadCaptureModal({ 
  categoryId, 
  onClose, 
  questions: initialQuestions, 
  runType, 
  isModal = true, 
  onPreviewComplete ,
  locale='IN'
}) {
  // --- 1. Core State: "What is shown to the user?" ---
  const [activeQuestions, setActiveQuestions] = useState([]); 
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [otherInputs, setOtherInputs] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Cache to store "Full Question Sets" for every category we encounter
  // Structure: { categoryId: [All Questions Sorted] }
  // We need this to "Restore" the list if a user undoes a redirect.
  const categoryCache = useRef({});

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleApiKey,
    libraries: MAP_LIBRARIES,
    region: 'IN',
    version: 'weekly',
  });

  // --- 2. Initialization ---
  useEffect(() => {
    if (initialQuestions && initialQuestions.length > 0) {
      setAnswers({});
      setOtherInputs({});
      setSubmitted(false);
      setCurrentIdx(0);

      // Process and set Initial State
      const processed = initialQuestions.map(sortQuestionOptions);
      
      // Seed the cache with the starting category
      if (categoryId) {
        categoryCache.current[categoryId] = processed;
      }
      
      setActiveQuestions(processed);
    } else {
      setActiveQuestions([]);
    }
  }, [initialQuestions, categoryId]);

  // --- 3. Fetch Helper ---
  const getCategoryQuestions = async (catId) => {
    // Return from cache if available
    if (categoryCache.current[catId]) {
      return categoryCache.current[catId];
    }
    
    // Fetch from API
    try {
      setLoading(true);
      const res = await api.get(`/admin/categories/cat-qna/category/${catId}`, { params: { locale: locale } });
      const processed = res.data.map(sortQuestionOptions);
      
      // Store in cache
      categoryCache.current[catId] = processed;
      return processed;
    } catch (err) {
      console.error("Fetch failed", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // --- 4. Logic Engine: "The Swap" ---
  // This function is called every time an answer changes.
  const updateQuestionPath = async (questionId, newValue) => {
    
    // Find where we are in the active list
    const qIndex = activeQuestions.findIndex(q => q.id === questionId);
    if (qIndex === -1) return;

    const currentQuestion = activeQuestions[qIndex];
    let nextCategoryTargetId = null;

    // A. Check for Redirect Logic
    if (currentQuestion.conditional_logic) {
      // Logic is keyed by Option ID, so find the Option ID for the selected Value
      // Note: 'newValue' matches option.value
      const selectedOption = currentQuestion.options?.find(opt => opt.value === newValue);
      
      if (selectedOption) {
        const rules = currentQuestion.conditional_logic[String(selectedOption.id)];
        const redirectRule = rules?.find(r => r.action === 'redirect');
        if (redirectRule) {
          nextCategoryTargetId = redirectRule.category_id;
        }
      }
    }

    // B. Handle the List Manipulation
    if (nextCategoryTargetId) {
      // --- CASE: REDIRECT ---
      // 1. Check if we are already showing this category (optimization)
      // We check if the *next* question in the list belongs to the target category
      const nextQ = activeQuestions[qIndex + 1];
      if (nextQ && nextQ.category_id === nextCategoryTargetId) {
         return; // Path is already correct
      }

      // 2. Fetch & Swap
      const newQuestions = await getCategoryQuestions(nextCategoryTargetId);
      
      // 3. Construct new list: [Head] + [New Body]
      // Head = Questions 0 to current
      const head = activeQuestions.slice(0, qIndex + 1);
      
      // Update State
      setActiveQuestions([...head, ...newQuestions]);

    } else {
      // --- CASE: RESTORE (No Redirect) ---
      
      // We need to check if we are currently in a "Redirected State" that needs undoing.
      // We look at the current question's category.
      const currentCatId = currentQuestion.category_id || categoryId; // Fallback to prop if missing on obj
      
      // Retrieve the FULL original list for this category from cache
      const fullOriginalList = categoryCache.current[currentCatId];
      
      if (fullOriginalList) {
        // Find where the current question sits in its *original* category list
        const originalIndex = fullOriginalList.findIndex(q => q.id === questionId);
        
        if (originalIndex !== -1) {
            // We want to restore the "Tail" of this category
            const originalTail = fullOriginalList.slice(originalIndex + 1);
            
            // Check if our current active list already matches this restoration
            // (Optimization to avoid re-renders)
            const currentNextQ = activeQuestions[qIndex + 1];
            const originalNextQ = originalTail[0];
            
            // If the next question in Active is different from Original, we restore.
            if ((currentNextQ?.id !== originalNextQ?.id) || (activeQuestions.length === qIndex + 1 && originalTail.length > 0)) {
                const head = activeQuestions.slice(0, qIndex + 1);
                setActiveQuestions([...head, ...originalTail]);
            }
        }
      }
    }
  };

  // --- 5. Visibility Calculation ---
  // We use the standard helper on our dynamic 'activeQuestions' list
  const visibleQuestions = useMemo(() => {
    // We must remap order_index to ensure they are sequential (0,1,2...) for the helper
    // otherwise the helper might hide questions if indices overlap or reset
    const sequentialQuestions = activeQuestions.map((q, idx) => ({
        ...q,
        order_index: idx 
    }));
    return calculateVisibleQuestions(sequentialQuestions, answers);
  }, [activeQuestions, answers]);

  const currentQuestion = visibleQuestions[currentIdx];

  const getFormattedAnswer = (question) => {
    const val = answers[question.id];
    if (!val) return <span className="text-gray-400 italic">Skipped</span>;

    if (question.question_type === 'single_select') {
        if (val === 'other') return <span className="font-medium">Other: {otherInputs[question.id]}</span>;
        const opt = question.options?.find(o => o.value === val);
        return <span className="font-medium">{opt ? opt.label : val}</span>;
    }

    if (question.question_type === 'multi_select') {
        const vals = Array.isArray(val) ? val : [val];
        return (
            <div className="flex flex-wrap gap-2">
                {vals.map((v, i) => {
                    const isOther = v === 'other';
                    const label = isOther ? `Other: ${otherInputs[question.id]}` : question.options?.find(o => o.value === v)?.label || v;
                    return (
                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-sm border border-blue-100">
                            {label}
                        </span>
                    );
                })}
            </div>
        );
    }

    if (question.question_type === 'location') {
        const address = typeof val === 'object' ? val.address : val;
        return <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-gray-500"/> {address}</span>;
    }

    if (question.question_type === 'date') {
        return <span className="flex items-center gap-1"><Calendar className="h-4 w-4 text-gray-500"/> {val.replace('T', ' ')}</span>;
    }

    return <span className="font-medium">{val}</span>;
  };
  // --- 6. Event Handlers ---

  const handleNext = () => {
    if (!visibleQuestions.length || !currentQuestion) return;
    const answer = answers[currentQuestion.id];
    
    // Simple Validation
    if (currentQuestion.is_required && !answer) {
       alert("This field is required");
       return;
    }

    if (currentIdx < visibleQuestions.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      generateAndReportUrlParams();
      setSubmitted(true);
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  const handleAnswerChange = async (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    await updateQuestionPath(id, value);
  };
  
  // (Multi-select usually doesn't trigger redirects, keeping logic simple)
  const handleMultiAnswerChange = (id, value, checked) => {
    setAnswers(prev => {
        const current = new Set(prev[id] || []);
        checked ? current.add(value) : current.delete(value);
        return { ...prev, [id]: Array.from(current) };
    });
  };

  const generateAndReportUrlParams = useCallback(() => {
    const params = {};
    Object.entries(answers).forEach(([qId, val]) => {
        // Only include answers for questions currently in the active path
        if (activeQuestions.find(q => q.id === parseInt(qId))) {
             // Basic param generation logic...
             params[`q${qId}`] = val;
        }
    });
    if (onPreviewComplete) onPreviewComplete(params);
  }, [answers, activeQuestions, onPreviewComplete]);

  // --- 7. Rendering (Render Input Helper) ---
  const renderInput = (question) => {
     if (!question) return null;
     const qId = question.id;
     const val = answers[qId];

     switch (question.question_type) {
        case 'text':
        case 'textarea':
             const InputComp = question.question_type === 'textarea' ? 'textarea' : 'input';
             return <InputComp className="border p-2 w-full rounded" value={val || ''} onChange={e => handleAnswerChange(qId, e.target.value)} />;
        
        case 'single_select':
             return (
                 <div className="space-y-2 mt-2">
                     {question.options?.map(opt => (
                         <label key={opt.id} className="flex items-center space-x-2 cursor-pointer">
                             <input 
                                type="radio" 
                                name={`q-${qId}`} 
                                checked={val === opt.value} 
                                onChange={() => handleAnswerChange(qId, opt.value)}
                                className="h-4 w-4 text-blue-600"
                             />
                             <span>{opt.label}</span>
                         </label>
                     ))}
                 </div>
             );
        case 'multi_select':
            return (
                <div className="space-y-2 mt-2">
                    {question.options?.map(opt => (
                        <label key={opt.id} className="flex items-center space-x-2 cursor-pointer">
                            <input 
                               type="checkbox" 
                               checked={(val || []).includes(opt.value)} 
                               onChange={(e) => handleMultiAnswerChange(qId, opt.value, e.target.checked)}
                               className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span>{opt.label}</span>
                        </label>
                    ))}
                </div>
            );
        case 'location':
             return <div className="min-h-[200px]"><LocationAutocomplete isLoaded={isLoaded} onSelect={(l) => handleAnswerChange(qId, l)} initialValue={val?.address} /></div>;
        
        case 'date':
             return <input type="date" className="border p-2 rounded w-full" value={val?.split('T')[0] || ''} onChange={e => handleAnswerChange(qId, e.target.value)} />;
        
        default:
             return <div className="text-red-500">Unsupported Type</div>;
     }
  };

  // --- 8. Main Render ---
  
  if (loading && !currentQuestion) {
      return <div className="p-10 flex justify-center"><Loader2 className="animate-spin h-8 w-8 text-blue-600"/></div>;
  }

  if (submitted) {
      return (
          <div className={isModal ? "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" : "p-4"}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] min-h-[500px]">
                <div className="p-6 text-center border-b">
                    <div className="bg-green-100 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600"/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Request Summary</h2>
                    <p className="text-gray-500">Please review your details below.</p>
                </div>
                
                <div className="p-6 flex-grow overflow-y-auto bg-gray-50/50">
                    <div className="space-y-4">
                        {/* Iterate activeQuestions to show what was actually asked */}
                        {visibleQuestions.map((q, idx) => (
                            <div key={q.id} className="bg-white p-4 rounded border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-start mb-1">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Question {idx + 1}</h3>
                                </div>
                                <p className="text-sm font-medium text-gray-900 mb-2">{q.label}</p>
                                <div className="text-gray-700 bg-gray-50 p-2 rounded border border-gray-100">
                                    {getFormattedAnswer(q)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 border-t bg-white rounded-b-lg flex justify-center gap-3">
                    <Button variant="outline" onClick={() => { setSubmitted(false); setAnswers({}); setOtherInputs({}); setCurrentIdx(0); }}>
                        Test Again
                    </Button>
                    {isModal && <Button onClick={onClose}>Close</Button>}
                </div>
            </div>
          </div>
      );
  }

  if (!visibleQuestions.length && !loading) {
      return <div className="p-6 bg-white rounded shadow text-center">No questions available. <Button onClick={onClose} className="mt-4">Close</Button></div>;
  }

  return (
    <div className={isModal ? "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" : "p-4"}>
       <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] min-h-[500px]">
           {/* Header */}
           <div className="p-4 border-b flex justify-between items-center">
               <div>
                   <h2 className="text-lg font-bold">Service Request</h2>
                   <p className="text-xs text-gray-500">Step {currentIdx + 1} of {visibleQuestions.length}</p>
               </div>
               <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-600">&times;</button>
           </div>

           {/* Body */}
           <div className="p-6 flex-grow overflow-y-auto">
               {currentQuestion && (
                   <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                       <h3 className="text-lg font-medium mb-1">
                           {currentQuestion.label} <span className="text-red-500">{currentQuestion.is_required ? '*' : ''}</span>
                       </h3>
                       {currentQuestion.nudge_text && <p className="text-sm text-gray-400 mb-4">{currentQuestion.nudge_text}</p>}
                       {renderInput(currentQuestion)}
                   </div>
               )}
           </div>

           {/* Footer */}
           <div className="p-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
               <Button variant="ghost" onClick={handleBack} disabled={currentIdx === 0}>
                   <ArrowLeft className="mr-2 h-4 w-4"/> Back
               </Button>
               
               <div className="flex gap-2">
                   <Button variant="outline" size="icon" onClick={generateAndReportUrlParams} title="Generate URL"><Link2 className="h-4 w-4"/></Button>
                   <Button onClick={handleNext} className="min-w-[100px] bg-blue-600 hover:bg-blue-700">
                       {loading ? <Loader2 className="animate-spin h-4 w-4"/> : (
                           currentIdx === visibleQuestions.length - 1 ? 'Submit' : <span className="flex items-center">Next <ArrowRight className="ml-2 h-4 w-4"/></span>
                       )}
                   </Button>
               </div>
           </div>
       </div>
    </div>
  );
}